import { FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from '@ramsey-design-system/input';
import FormField from '@ramsey-design-system/form-field';
import { useWizard } from 'react-use-wizard';
import {
  useGAFormTracking,
  useHotjarExitIntentSurvey
} from '../../../shared/hooks';
import { useMatcherContext } from '../context';
import { getAnsweredValue } from '../flow/types';
import {
  trackStepCompleted,
  trackStepErrorViewed
} from '../../../shared/analytics';
import {
  getMatcherFormName,
  matcherCapitalizeName,
  useMatcherFieldSize,
  useTrackMatcherStepStarted
} from '../utils';
import {
  MatcherStandardNextButton,
  MatcherStandardStepTitle,
  MatcherStepWrapper
} from '../components';
import {
  consolidateMatcherFirstAndLastNameErrors,
  matcherFullNameInfoSchema,
  MatcherNameStepFields
} from '../schemas/MatcherFullNameSchema';

const STEP_NAME = 'matcher_name_step';
const STEP_LABEL = 'Before we begin, what should we call you?';

function MatcherNameStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  useHotjarExitIntentSurvey();
  const { nextStep, activeStep, goToStep } = useWizard();
  const { matcherAnswers, updateMatcherAnswer, stepQueue } =
    useMatcherContext();
  const fieldSize = useMatcherFieldSize();
  const existingNameParts = getAnsweredValue(matcherAnswers.name);
  const [existingFirstName, existingLastName] = Array.isArray(existingNameParts)
    ? existingNameParts
    : ['', ''];
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<MatcherNameStepFields>({
    defaultValues: {
      first_name: existingFirstName,
      last_name: existingLastName
    },
    resolver: yupResolver(matcherFullNameInfoSchema)
  });
  // eslint-disable-next-line react-hooks/incompatible-library
  const fullName = [watch('first_name'), watch('last_name')]
    .filter(Boolean)
    .join(' ');

  const consolidatedError = consolidateMatcherFirstAndLastNameErrors(errors);

  useTrackMatcherStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function onValidSubmit(values: MatcherNameStepFields) {
    updateMatcherAnswer('name', {
      status: 'answered',
      value: [values.first_name, values.last_name]
    });
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['First Name', 'Last Name'],
      answer_values: [values.first_name, values.last_name],
      form_name: getMatcherFormName()
    });
    await nextStep();
  }

  function onInvalidSubmit(errors: FieldErrors<MatcherNameStepFields>) {
    trackStepErrorViewed({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      error_type: 'validation',
      error_field: errors.first_name ? 'first_name' : 'last_name',
      error_message: consolidateMatcherFirstAndLastNameErrors(errors) ?? '',
      form_name: getMatcherFormName()
    });
  }

  function applyNameParts(fullName: string) {
    const nameParts = fullName
      .split(' ')
      .filter((parts) => parts.trim() !== '');
    const first = nameParts[0] || '';
    const last =
      nameParts.length > 1 ? nameParts[nameParts.length - 1] || '' : '';

    setValue('first_name', first);
    setValue('last_name', last);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    applyNameParts(e.target.value);
  }

  // autocapitalize="words" only hints mobile virtual keyboards - desktop browsers with a
  // physical keyboard ignore it entirely, so this is what actually capitalizes each word on
  // every platform. On blur (not on every keystroke) so it doesn't fight the caret position
  // while someone's still typing.
  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const capitalized = matcherCapitalizeName(e.target.value);
    e.target.value = capitalized;
    applyNameParts(capitalized);
  }

  return (
    // MatcherSearchStep is a transient step that auto-advances past itself once a match is
    // already found for the current answers (see its skipStep check) - landing on it going
    // backward would just bounce straight back here. Skip past it to MatcherMeetingStep instead
    // of the chevron's default single-step back. Derived from stepQueue's own index of
    // 'meeting' rather than a fixed offset, so a later reorder can't silently break this.
    <MatcherStepWrapper onBack={() => goToStep(stepQueue.indexOf('meeting'))}>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}
        className="flex-1 flex flex-col"
      >
        <MatcherStandardStepTitle noSubtitle>
          What should we call you?
        </MatcherStandardStepTitle>

        <div className="space-y-2 mb-4">
          <FormField
            id="full_name"
            label="Full Name"
            size={fieldSize}
            invalid={!!errors.first_name || !!errors.last_name}
            errors={consolidatedError ? [consolidatedError] : []}
          >
            {() => (
              <Input
                size={fieldSize}
                invalid={!!errors.first_name || !!errors.last_name}
                id="full_name"
                data-testid="tax_matcher_full_name_input"
                autoComplete="name"
                autoCapitalize="words"
                className="placeholder:text-gray-50 placeholder:text-3 sm:placeholder:text-4"
                placeholder="e.g., John Smith"
                defaultValue={fullName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )}
          </FormField>

          {/* The below inputs are used to hold state for useForm */}
          <input
            {...register('first_name')}
            className={'hidden'}
            aria-hidden={true}
            tabIndex={-1}
          />
          <input
            {...register('last_name')}
            className={'hidden'}
            aria-hidden={true}
            tabIndex={-1}
          />
        </div>

        <div className="mt-auto md:mt-0">
          <MatcherStandardNextButton />
        </div>
      </form>
    </MatcherStepWrapper>
  );
}

export { MatcherNameStep };
